import React from "react";
import { Skeleton } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useAuth } from "../../../../providers/AuthProvider";
import {
  isResourcePending,
  usePlatformResource,
} from "../../hooks/usePlatformResource";
import ScrollableWrapper from "../common/ScrollableWrapper";
import TimelineView from "./timeline/TimelineView";
import useHistoryScope from "./useHistoryScope";

/**
 * History → Timeline, in the client workspace and the customer's own
 * workspace (see `useHistoryScope.showTimeline`).
 *
 * The chart's metrics come from two platform resources — `biomarkers` (the
 * readings with their range segments) and `report` (the PNOĒ metric scores).
 * The tab waits for the biomarkers only: the opening selection is biomarkers
 * and a wearable, and the report (GetReportv2, ~8s) is needed just for the
 * "PNOE metrics" section of Filters, which fills in when it arrives. The
 * events half loads its own listings.
 */
const TimelineTab = () => {
  const theme: Theme = useTheme();
  const { AuthUser } = useAuth();
  const scope = useHistoryScope();
  const reportStatus = usePlatformResource(
    "report",
    scope.subjectId,
    AuthUser?.role
  );
  const biomarkersStatus = usePlatformResource(
    "biomarkers",
    scope.subjectId,
    AuthUser?.role
  );

  if (isResourcePending(biomarkersStatus)) {
    return (
      <Skeleton
        variant="rectangular"
        sx={{
          width: "100%",
          height: "560px",
          borderRadius: "8px",
          backgroundColor: theme.colorTokens.N[30],
        }}
      />
    );
  }

  // The History screen is bounded to the viewport; a tall timeline (five
  // metrics plus every service lane) scrolls here rather than clipping.
  return (
    <ScrollableWrapper>
      <TimelineView scope={scope} metricsLoading={isResourcePending(reportStatus)} />
    </ScrollableWrapper>
  );
};

export default TimelineTab;
